import { useState } from "react";
import { Link } from "wouter";
import { useAction, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import {
  ArrowLeft, Shield, MapPin, CalendarClock, UserCheck, Star,
  ShieldCheck, Building2, Archive, Wrench, RefreshCw, GitMerge, KeyRound, Bell,
  Music4,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer";
import { Button } from "../components/ui/button";
import { ModerationQueue } from "../components/admin/ModerationQueue";
import { TrustedEmailsManager } from "../components/admin/TrustedEmailsManager";
import { UserVerificationsQueue } from "../components/admin/UserVerificationsQueue";
import { ModeratorManager } from "../components/admin/ModeratorManager";
import { VenueClaimsQueue } from "../components/admin/VenueClaimsQueue";
import { ArchivePanel } from "../components/admin/ArchivePanel";
import { VenueOrganizersQueue } from "../components/admin/VenueOrganizersQueue";
import { NotificationSettings } from "../components/admin/NotificationSettings";
import { DanceStyleManager } from "../components/admin/DanceStyleManager";
import { useModeratorRole } from "../hooks/useModeratorRole";

type Tab = "events" | "users" | "trusted" | "moderators" | "venues" | "venueAccess" | "archive" | "styles" | "notifications" | "tools";

const ALL_TABS: { id: Tab; label: string; icon: LucideIcon }[] = [
  { id: "events",      label: "Pending Events",     icon: CalendarClock },
  { id: "users",       label: "User Verifications", icon: UserCheck },
  { id: "trusted",     label: "Trusted Emails",     icon: Star },
  { id: "moderators",  label: "Moderators",         icon: ShieldCheck },
  { id: "venues",      label: "Venue Claims",       icon: Building2 },
  { id: "venueAccess", label: "Venue Access",       icon: KeyRound },
  { id: "archive",     label: "Archive",            icon: Archive },
  { id: "styles",      label: "Dance Styles",       icon: Music4 },
  { id: "notifications", label: "Notifications",    icon: Bell },
  { id: "tools",       label: "Tools",              icon: Wrench },
];

const TABS_BY_ROLE: Record<string, Tab[]> = {
  volunteer: ["events"],
  moderator: ["events", "users", "trusted", "moderators", "venues", "venueAccess", "archive", "styles", "tools"],
  admin:     ["events", "users", "trusted", "moderators", "venues", "venueAccess", "archive", "styles", "notifications", "tools"],
};

type GeocodeResult = { eventId: string; address: string; success: boolean; error?: string };

function ToolsPanel() {
  const backfill = useAction(api.geocoding.backfillGeocode);
  const extendSeries = useMutation(api.recurring.extendRecurringSeries);
  const migrateOld = useMutation(api.recurring.migrateOldRecurringPatterns);

  const [geocodeRunning, setGeocodeRunning] = useState(false);
  const [geocodeResults, setGeocodeResults] = useState<GeocodeResult[] | null>(null);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);

  const [extendRunning, setExtendRunning] = useState(false);
  const [extendResult, setExtendResult] = useState<{ totalCreated: number } | null>(null);
  const [extendError, setExtendError] = useState<string | null>(null);

  const [migrateRunning, setMigrateRunning] = useState(false);
  const [migrateResult, setMigrateResult] = useState<{ migratedPatterns: number; createdEvents: number } | null>(null);
  const [migrateError, setMigrateError] = useState<string | null>(null);

  const handleBackfill = async () => {
    setGeocodeRunning(true);
    setGeocodeResults(null);
    setGeocodeError(null);
    try {
      const data = await backfill({});
      setGeocodeResults(data);
    } catch (e) {
      setGeocodeError(String(e));
    } finally {
      setGeocodeRunning(false);
    }
  };

  const handleExtendSeries = async () => {
    setExtendRunning(true);
    setExtendResult(null);
    setExtendError(null);
    try {
      const data = await extendSeries({});
      setExtendResult(data);
    } catch (e) {
      setExtendError(String(e));
    } finally {
      setExtendRunning(false);
    }
  };

  const handleMigrateOld = async () => {
    setMigrateRunning(true);
    setMigrateResult(null);
    setMigrateError(null);
    try {
      const data = await migrateOld({});
      setMigrateResult(data);
    } catch (e) {
      setMigrateError(String(e));
    } finally {
      setMigrateRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border border-border rounded-lg p-5">
        <div className="flex items-center gap-2 mb-2">
          <GitMerge className="w-4 h-4 text-primary" />
          <h2 className="font-semibold text-foreground">Migrate Legacy Recurring Events</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Converts old-style recurring events (template + virtual instances) to the new model
          (20 real event records). Safe to run multiple times — skips patterns already migrated.
          Archives the old template events automatically.
        </p>
        <Button onClick={handleMigrateOld} disabled={migrateRunning} size="sm">
          {migrateRunning ? "Running..." : "Migrate Legacy Recurring Events"}
        </Button>

        {migrateError && <p className="mt-3 text-sm text-red-500">{migrateError}</p>}
        {migrateResult !== null && (
          <p className="mt-3 text-sm text-green-700">
            {migrateResult.migratedPatterns === 0
              ? "No legacy patterns found — all series are already on the new model."
              : `Migrated ${migrateResult.migratedPatterns} series, created ${migrateResult.createdEvents} events.`}
          </p>
        )}
      </div>

      <div className="border border-border rounded-lg p-5">
        <div className="flex items-center gap-2 mb-2">
          <RefreshCw className="w-4 h-4 text-primary" />
          <h2 className="font-semibold text-foreground">Extend Recurring Series</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Tops up every active recurring series so each always has 20 upcoming instances.
          This also runs automatically every day at 6 AM UTC.
        </p>
        <Button onClick={handleExtendSeries} disabled={extendRunning} size="sm">
          {extendRunning ? "Running..." : "Extend Recurring Series Now"}
        </Button>

        {extendError && <p className="mt-3 text-sm text-red-500">{extendError}</p>}
        {extendResult !== null && (
          <p className="mt-3 text-sm text-green-700">
            {extendResult.totalCreated === 0
              ? "All series already have 20+ upcoming events."
              : `Created ${extendResult.totalCreated} new event instance${extendResult.totalCreated !== 1 ? "s" : ""}.`}
          </p>
        )}
      </div>

      <div className="border border-border rounded-lg p-5">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-4 h-4 text-primary" />
          <h2 className="font-semibold text-foreground">Backfill Geocoding</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Geocodes events with an address but no coordinates. Retries previously failed events too (up to 10 at a time).
        </p>
        <Button onClick={handleBackfill} disabled={geocodeRunning} size="sm">
          {geocodeRunning ? "Running..." : "Run Geocoding Backfill"}
        </Button>

        {geocodeError && <p className="mt-3 text-sm text-red-500">{geocodeError}</p>}

        {geocodeResults !== null && (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-foreground">
              {geocodeResults.length === 0
                ? "No events needed geocoding."
                : `Processed ${geocodeResults.length} event${geocodeResults.length !== 1 ? "s" : ""}:`}
            </p>
            {geocodeResults.map((r) => (
              <div
                key={r.eventId}
                className={`rounded px-3 py-2 text-xs ${r.success ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}
              >
                <span className="font-medium">{r.success ? "✓" : "✗"}</span>{" "}
                <span className="font-mono">{r.address || "(no address)"}</span>
                {r.error && <span className="block mt-0.5 text-red-600">{r.error}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminPage() {
  const { role, isLoading, isAnyModerator } = useModeratorRole();
  const [activeTab, setActiveTab] = useState<Tab>("events");

  if (isLoading) {
    return (
      <PageContainer>
        <div className="py-12 text-center text-muted-foreground">Loading...</div>
      </PageContainer>
    );
  }

  if (!isAnyModerator) {
    return (
      <PageContainer>
        <div className="py-12 text-center">
          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Access Denied</h1>
          <p className="text-muted-foreground mb-4">
            You need moderator access to view this page.
          </p>
          <Button asChild variant="outline">
            <Link href="/events">Go Home</Link>
          </Button>
        </div>
      </PageContainer>
    );
  }

  const visibleTabIds = role ? (TABS_BY_ROLE[role] ?? ["events"]) : ["events"];
  const visibleTabs = ALL_TABS.filter((t) => visibleTabIds.includes(t.id));

  // If the active tab is no longer visible for this role, reset to first visible
  const safeActiveTab = visibleTabIds.includes(activeTab) ? activeTab : visibleTabIds[0];

  const roleLabel = role === "admin" ? "Admin" : role === "moderator" ? "Moderator" : "Volunteer";

  return (
    <PageContainer>
      <div className="mb-6">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/dashboard">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </Button>
      </div>

      <header className="mb-6">
        <h1 className="font-display text-3xl text-foreground mb-2 flex items-center gap-3">
          <Shield className="w-8 h-8 text-primary" />
          Admin Panel
        </h1>
        <p className="text-sm text-muted-foreground">
          Signed in as{" "}
          <span className="font-medium capitalize text-foreground">{roleLabel}</span>
        </p>
      </header>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        {/* Sidebar nav */}
        <nav className="w-full md:w-44 shrink-0">
          <ul className="flex flex-col gap-0.5">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const active = safeActiveTab === tab.id;
              return (
                <li key={tab.id}>
                  <button
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    {tab.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {safeActiveTab === "events" && <ModerationQueue />}
          {safeActiveTab === "users" && <UserVerificationsQueue />}
          {safeActiveTab === "trusted" && <TrustedEmailsManager />}
          {safeActiveTab === "moderators" && <ModeratorManager />}
          {safeActiveTab === "venues" && <VenueClaimsQueue />}
          {safeActiveTab === "venueAccess" && <VenueOrganizersQueue />}
          {safeActiveTab === "archive" && <ArchivePanel />}
          {safeActiveTab === "styles" && <DanceStyleManager />}
          {safeActiveTab === "notifications" && <NotificationSettings />}
          {safeActiveTab === "tools" && <ToolsPanel />}
        </div>
      </div>
    </PageContainer>
  );
}
